// Global UI stores: command palette, toasts, document drawer.
// Kept outside component files so react-refresh stays reliable.
import { create } from 'zustand';
import { documentsService, type DocumentDetail } from '@/services/documents';
import i18n from '@/i18n';

// ─── Command palette ───
interface PaletteState {
  open: boolean;
  setOpen: (open: boolean) => void;
}
export const usePalette = create<PaletteState>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

// ─── Copilot (VLT-250) ───
interface CopilotState {
  open: boolean;
  setOpen: (open: boolean) => void;
}
export const useCopilot = create<CopilotState>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

// ─── Toasts ───
export interface ToastItem {
  id: number;
  message: string;
  tone: 'success' | 'info';
}
interface ToastState {
  toasts: ToastItem[];
  push: (message: string, tone?: ToastItem['tone']) => void;
  dismiss: (id: number) => void;
}
let nextId = 1;
export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  push: (message, tone = 'success') => {
    const id = nextId++;
    set((s) => ({ toasts: [...s.toasts, { id, message, tone }] }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), 4200);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));
/** Imperative helper — usable from services, stores and components alike. */
export const toast = (message: string, tone?: ToastItem['tone']) =>
  useToasts.getState().push(message, tone);

// ─── Document drawer ───
interface DrawerState {
  doc: DocumentDetail | null;
  open: (id: string) => void;
  close: () => void;
}
export const useDocumentDrawer = create<DrawerState>((set) => ({
  doc: null,
  open: (id) => {
    // Si la lectura falla, el cajón no se abre y se avisa (el store sigue sano).
    documentsService.getDocument(id).then(
      (doc) => doc && set({ doc }),
      () => toast(i18n.t('errors.generic.what'), 'info'),
    );
  },
  close: () => set({ doc: null }),
}));
